import * as React from 'react';
import * as classes from '@common/components/tabs/tabs.scss';
import * as ReactDOM from 'react-dom';

interface TabsState {
  TabsNode?: Element | null;
  selectedTabIndex: number;
}

interface TabsProperties {
  onTabChange?: (oldTabIndex: number, newTabIndex: number) => void;
  tabShouldChange?: (oldTabIndex: number, newTabIndex: number) => boolean;
  left?: boolean;
}

export class Tabs extends React.Component<TabsProperties, TabsState> {
  private resizeTimeout: NodeJS.Timer | null;

  constructor(props: TabsProperties) {
    super(props);
    this.state = {
      TabsNode: null,
      selectedTabIndex: 0
    };
  }

  componentDidMount() {
    // Must get the node once mounted to get client width for calcs
    this.setState({ TabsNode: ReactDOM.findDOMNode(this) });

    window.addEventListener('resize', this.resizeThrottler, false);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.resizeThrottler, false);
  }

  resizeThrottler = () => {
    if (!this.resizeTimeout) {
      this.resizeTimeout = setTimeout(() => {
        this.resizeTimeout = null;
        this.handleResize();
      }, 1000);
    }
  };

  handleResize = () => {
    this.setState({ TabsNode: ReactDOM.findDOMNode(this) });
  };

  handleTabClick = (index: number) => {
    if (this.props.onTabChange && index !== this.state.selectedTabIndex) {
      if (!this.props.tabShouldChange || this.props.tabShouldChange(this.state.selectedTabIndex, index)) {
        this.props.onTabChange(this.state.selectedTabIndex, index);
        this.setState({ selectedTabIndex: index });
      }
    } else {
      this.setState({ selectedTabIndex: index });
    }
  };

  handleKeyDown = (e: any, index: number) => {
    const keyCode = e.keyCode;
    if (keyCode === 13 || keyCode === 32) {
      this.handleTabClick(index);
    }
  };

  composedClicks = (arg: any, parentTabClick: (x: any) => void, childClick?: (x: any) => void) => {
    parentTabClick(arg);
    if (childClick) {
      childClick(arg);
    }
  };

  renderTabs = () => {
    return React.Children.map(this.props.children, (child: any, index: number) => {
      return React.cloneElement(child, {
        title: child.props.title,
        onClick: (x: any) => this.composedClicks(x, this.handleTabClick, child.props.onClick),
        onKeyDown: this.handleKeyDown,
        tabIndex: index,
        isActiveTab: index === this.state.selectedTabIndex,
        className: index === this.state.selectedTabIndex && 'active'
      });
    });
  };

  handleBlur = (e: any) => {
    e.preventDefault();
  };

  renderTabContent = () => {
    return React.Children.map(this.props.children, (child: any, index: number) => {
      return (
        <li
          style={{ width: this.getItemListWidth() + 'px' }}
          className={index === this.state.selectedTabIndex ? classes.active : ''}
          onBlur={this.handleBlur}
        >
          <div className={classes.contentContainer}>
            <div className={classes.hide}>{child.props.children}</div>
          </div>
        </li>
      );
    });
  };

  getTotalChildren = () => {
    let total: number = 0;
    React.Children.map(this.props.children, (child: any, index: number) => total++);

    return total;
  };

  getListWidth = () => {
    if (this.state.TabsNode) {
      return this.state.TabsNode!.clientWidth * this.getTotalChildren();
    }
    return 0;
  };

  getItemListWidth = () => {
    return this.getListWidth() / this.getTotalChildren() - 40; // 40 is for padding on sides
  };

  calcTranslate = () => {
    const translateAmount = -(this.state.selectedTabIndex * (this.getItemListWidth() + 40));

    return `translateX(${translateAmount}px)`;
  };

  render() {
    return (
      <div className={classes.tabs}>
        <ul className={[classes.tabLabels, 'list-unstyled', this.props.left ? classes.left : ''].join(' ')}>
          {this.renderTabs()}
        </ul>
        <div className={classes.tabContainer}>
          <ul
            style={{ width: this.getListWidth().toString() + 'vw', transform: this.calcTranslate() }}
            className={[classes.tabContent, 'list-unstyled'].join(' ')}
          >
            {this.renderTabContent()}
          </ul>
        </div>
      </div>
    );
  }
}

export const Tab = (props: {
  title: string;
  onClick?: Function;
  onKeyDown?: Function;
  tabIndex?: number;
  isActiveTab?: boolean;
  children?: React.ReactNode;
  showError?: boolean;
}) => {
  return (
    <li
      className={props.isActiveTab ? classes.active + ' pointer ellipsis' : 'pointer ellipsis'}
      onClick={() => props.onClick!(props.tabIndex)}
      onKeyDown={e => props.onKeyDown!(e, props.tabIndex)}
      tabIndex={0}
    >
      {props.title}{' '}
      {props.showError && <i className={['fas fa-exclamation-triangle red-text', classes.tabError].join(' ')} />}
    </li>
  );
};



// WEBPACK FOOTER //
// ./src/common/components/tabs/tabs.component.tsx